import React, { useEffect, useState, useRef } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Pagination from "../../../utils/admin/pagination/Pagination";
import {
  getSurveyData,
  deleteSurvey,
  getCategoriesForXMOT,
  updateXmotStatus,
  cloneXMOTSurvey,
} from "../../../services/xmotSurvey/survey";
import {
  SP_CLIENT_ID_DEV,
  SP_CLIENT_ID_STAGE,
  SP_CLIENT_ID_LIVE,
  SET_STUDY_ID,
  SET_SESSION_ID,
  SET_SESSION_CODE,
} from "../../../utils/constants";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";

const getClientIdFromUrl = (hostname) => {
  if (hostname.includes("app-dev.panel.qualsights.com")) {
    return SP_CLIENT_ID_DEV;
  } else if (hostname.includes("app-stage.panel.qualsights.com")) {
    return SP_CLIENT_ID_STAGE;
  } else if (
    hostname.includes("app.panel.qualsights.com") ||
    hostname.includes("platform.usesights.com") ||
    hostname.includes("usesights.qualsights.com")
  ) {
    return SP_CLIENT_ID_LIVE;
  } else {
    return SP_CLIENT_ID_DEV; // for local development
  }
};

const SurveyListOld = () => {
  const [surveyData, setSurveyData] = useState([]);
  const [totalSurveys, setTotalSurveys] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [page, setPage] = useState(1);
  const toast = useRef(null);
  const limit = 50;
  const { token, user } = useSelector((state) => state.auth);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const debounceTimeoutRef = useRef(null);
  const [showDialogForCategory, setShowDialogForCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStudy, setSelectedStudy] = useState("");
  const [selectedTriggerType, setSelectedTriggerType] = useState("");
  const [showCategoryError, setShowCategoryError] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const hostname = window.location.hostname;
  const lastSegment = location.pathname.split("/").pop();
  const [isCloning, setIsCloning] = useState(false);
  const [dummySurvey, setDummySurvey] = useState({
    SessionName: "",
    CompletedCount: 0,
    StudyId: "",
    SessionId: null,
    SessionCode: "",
    TriggerId: "",
    TriggerType: "",
    SessionStatus: false,
  });
  const [showCloneLoader, setShowCloneLoader] = useState(false);
  const tdStyle = { verticalAlign: "middle" };
  const id = lastSegment.split("-").pop();
  const fetchSurveyList = () => {
    setLoading(true);
    const reqBody = {
      client_id:
        user?.type === "superadmin" ? parseInt(id) : parseInt(user?.client_id),
      page: page,
      limit: limit,
      q: searchText,
    };

    getSurveyData(token, reqBody)
      .then((response) => {
        const list = response.data.sessions_list || [];
        setSurveyData(list);
        setTotalSurveys(response.data.total);
        dispatch({
          type: SET_STUDY_ID,
          payload: "",
        });
      })
      .catch((error) => {
        console.error("Error fetching survey data:", error);
        setSurveyData([]);
        setTotalSurveys(0);
      })
      .finally(() => setLoading(false));
  };

  const showError = (message) => {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  };

  const getCategoryForXMOT = () => {
    const reqBody = {
      client_id:
        user?.type === "superadmin" ? parseInt(id) : parseInt(user?.id),
    };
    getCategoriesForXMOT(token, reqBody)
      .then((response) => {
        if (response.status === 200) {
          const categories = response.data.data;
          setCategoryData(categories);

          // Check if there's only one category and set selectedCategory accordingly
          if (categories.length === 1) {
            const singleCategory = categories[0];
            setSelectedCategory(
              `${singleCategory.category_id}-${singleCategory.study_id}-${singleCategory.category_type}`,
            );
            setSelectedStudy(singleCategory.study_id);
            setSelectedTriggerType(singleCategory.category_type);
          } else {
            setSelectedCategory(""); // Reset if there are multiple categories
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  };

  useEffect(() => {
    getCategoryForXMOT();
  }, []);

  const showSuccess = (msg) => {
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: msg,
      life: 3000,
    });
  };

  const handleDeleteSurvey = (study_id, session_id) => {
    const reqBody = {
      id: parseInt(getClientIdFromUrl(hostname)),
      pid: study_id,
      session_id: session_id,
    };

    deleteSurvey(token, reqBody)
      .then((response) => {
        if (response.data.success) {
          showSuccess(response.data.msg);
          fetchSurveyList(); // Refresh list
        } else {
          console.error("Delete failed:", response.data.message);
        }
      })
      .catch((error) => {
        console.error("Delete error:", error);
      });
  };

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // If page changed (user pagination), fetch immediately
    if (searchText === "") {
      fetchSurveyList();
      return;
    }

    // Else, debounce when searchText changes
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSurveyList();
    }, 500);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [page, searchText]);

  // Pagination handlers
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalSurveys);
  const isPrevDisabled = page === 1;
  const isNextDisabled = end >= totalSurveys;
  const handlePrevPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalSurveys / limit);
    if (page < maxPage) setPage((prev) => prev + 1);
  };

  const handleCreateSurvey = () => {
    if (categoryData.length === 1) {
      const singleCategory = categoryData[0];
      dispatch({
        type: SET_STUDY_ID,
        payload: singleCategory.study_id,
      });
      const path =
        user.type === "superadmin"
          ? `/admin-panel/survey/create/${lastSegment}?pid=${singleCategory.study_id}&trigger_type=${singleCategory.category_type}&user_type=${user?.type}`
          : `/dashboard/survey/create?pid=${singleCategory.study_id}&trigger_type=${singleCategory.category_type}&user_type=${user?.type}`;
      navigate(path);
    } else {
      if (!selectedCategory) {
        setShowCategoryError(true);
        return;
      }

      const path =
        user.type === "superadmin"
          ? `/admin-panel/survey/create/${lastSegment}?pid=${selectedStudy}&trigger_type=${selectedTriggerType}&user_type=${user?.type}`
          : `/dashboard/survey/create?pid=${selectedStudy}&trigger_type=${selectedTriggerType}&user_type=${user?.type}`;
      navigate(path);
    }
  };
  const onAddSurveyClick = () => {
    const length = categoryData?.length || 0;
    if (length === 0) {
      // alert("No categories available to create a survey.");
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "No categories available to create a survey.",
        life: 3000,
      });
    } else if (length === 1) {
      handleCreateSurvey();
    } else {
      setShowDialogForCategory(true);
    }
  };

  const handleViewResultsClick = (studyId, sessionId, sessionCode) => {
    dispatch({
      type: SET_STUDY_ID,
      payload: studyId,
    });
    dispatch({
      type: SET_SESSION_ID,
      payload: sessionId,
    });
    dispatch({
      type: SET_SESSION_CODE,
      payload: sessionCode,
    });
    const path =
      user.type === "superadmin"
        ? `/admin-panel/survey/view-results/${lastSegment}`
        : `/dashboard/survey/view-results`;
    navigate(path);
  };

  const scrollToElement = (elementId, offset = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const topPos =
        element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: topPos,
        behavior: "smooth",
      });
    }
  };

  const handleCloneClick = (survey) => {
    setIsCloning(true);
    setDummySurvey({
      ...survey,
      SessionName: "Clone of " + (survey.SessionName || ""),
      SessionId: survey.SessionId,
      StudyId: survey.StudyId,
      TriggerId: survey.TriggerId,
      TriggerType: survey.TriggerType,
      CompletedCount: 0,
      SessionStatus: false,
    });
    setTimeout(() => {
      scrollToElement("clonedRow", 80);
    }, 300);
  };

  const handleDeleteClick = (survey) => {
    setSelectedSurvey(survey);
    setVisible(true);
  };

  const handleCancelClone = () => {
    setIsCloning(false);
    setDummySurvey({
      SessionName: "",
      CompletedCount: 0,
      StudyId: "",
      SessionId: null,
      SessionCode: "",
      TriggerId: "",
      TriggerType: "",
      SessionStatus: false,
    });
  };

  const handleAcceptClone = () => {
    if (!dummySurvey.SessionName || dummySurvey.SessionName.trim() === "") {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter a name for the cloned XMOT.",
        life: 3000,
      });
      return;
    }

    const reqBody = {
      id: parseInt(getClientIdFromUrl(hostname)),
      pid: dummySurvey.StudyId,
      session_id: dummySurvey.SessionId,
      session_name: dummySurvey.SessionName,
      trigger_type: dummySurvey.TriggerType,
      tid: dummySurvey.TriggerId,
    };

    setShowCloneLoader(true);

    cloneXMOTSurvey(token, reqBody)
      .then((response) => {
        if (response?.status === 500) {
          showError(response?.error || "Internal Server Error");
          setShowCloneLoader(false);
          return;
        }

        if (response?.data?.success) {
          showSuccess(response?.data?.msg);
          fetchSurveyList();
          handleCancelClone();
          setShowCloneLoader(false);
        } else if (response?.data?.msg) {
          showError(response?.data?.msg);
          setShowCloneLoader(false);
        }
      })
      .catch((error) => {
        if (error?.response?.status === 500) {
          showError(error?.response?.data?.error || "Internal Server Error");
        } else {
          showError(error?.response?.data?.msg || "Something went wrong");
        }
        setShowCloneLoader(false);
      });
  };

  const updateSurveyStatus = (event, sessionId, studyId) => {
    const isActive = event.value;
    const reqBody = {
      id: parseInt(getClientIdFromUrl(hostname)),
      session_id: sessionId,
      pid: studyId,
      enable_session: isActive ? 1 : 0,
    };

    updateXmotStatus(token, reqBody)
      .then((response) => {
        if (response?.status === 500) {
          showError(response?.error || "Internal Server Error");
          return;
        }

        if (response?.data?.success) {
          showSuccess(response?.data?.msg);
          fetchSurveyList();
        } else if (response?.data?.msg) {
          showError(response?.data?.msg);
        }
      })
      .catch((error) => {
        if (error?.response?.status === 500) {
          showError(error?.response?.data?.error || "Internal Server Error");
        } else {
          showError(error?.response?.data?.msg || "Something went wrong");
        }
      });
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    const [, studyId, triggerType] = value.split("-");

    setSelectedCategory(value);
    setSelectedStudy(studyId);
    setSelectedTriggerType(triggerType);

    dispatch({
      type: SET_STUDY_ID,
      payload: studyId,
    });
  };

  const closeCategoryDialog = () => {
    setShowDialogForCategory(false);
    dispatch({
      type: SET_STUDY_ID,
      payload: "",
    });
  };

  // Shared table headers
  const renderTableHead = () => (
    <thead className="clientsTableHead">
      <tr className="text-muted" style={{ fontSize: "12px" }}>
        <th style={{ width: "4%" }}>#</th>
        <th style={{ width: "25%" }}>XMOT Name</th>
        <th style={{ width: "12%" }}># Completed</th>
        <th style={{ width: "20%" }}>Category Name</th>
        <th style={{ width: "12%" }}>Analyze</th>
        <th style={{ width: "16%" }}>Action</th>
        <th>Active/Inactive</th>
      </tr>
    </thead>
  );

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
  };

  // Clear search input
  const clearSearch = () => {
    setSearchText("");
  };

  // Search + Pagination Row
  const renderSearchAndPagination = () => (
    <div className="d-flex justify-content-between align-items-center p-2">
      <div className="d-flex align-items-center">
        <span className="p-input-icon-left">
          <i
            className="pi pi-search"
            style={{ marginLeft: "320px", color: "#9e9e9e" }}
          />
          {searchText && ( // Conditionally render the clear icon
            <i
              className="pi pi-times"
              style={{
                cursor: "pointer",
                marginLeft: "290px",
                color: "#9e9e9e",
              }}
              onClick={clearSearch} // Clear the search input on click
            />
          )}
          <InputText
            placeholder="Search"
            style={{ borderRadius: "0px", width: "350px", fontWeight: "400" }}
            className="inputField"
            autoComplete="off"
            value={searchText} // Bind the input value to searchText
            onChange={(event) => handleSearch(event.target.value)}
          />
        </span>
      </div>
      <Pagination
        start={start}
        end={end}
        totalRecord={totalSurveys}
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
      />
    </div>
  );

  // Header & Add Button
  const renderHeader = () => (
    <div className="d-flex justify-content-between align-items-center px-3 py-2">
      <h6 className="fw-bold m-0 title">View Custom XMOTs</h6>
      <Button
        label="Create a Custom XMOT"
        className="px-3 py-2 fw-bold createClientBtn"
        onClick={onAddSurveyClick}
      />
    </div>
  );

  const renderSurveyTable = () => (
    <div className="table-responsive">
      {renderSearchAndPagination()}
      <Divider />
      <table className="table table-borderless surveyTable">
        {renderTableHead()}
        <tbody>
          {surveyData.map((survey, index) => (
            <tr key={index}>
              <td style={tdStyle}>{start + index}</td>
              <td
                style={tdStyle}
                dangerouslySetInnerHTML={{ __html: survey.SessionName || "-" }}
              ></td>
              <td style={tdStyle}>{survey.CompletedCount}</td>
              <td style={tdStyle}>
                {parseInt(survey.XmotClientId) === 11
                  ? "Demo category"
                  : survey.CatName}
              </td>
              <td style={tdStyle}>
                <span
                  className="text-primary"
                  style={{
                    cursor: "pointer",
                    marginRight: "10px",
                  }}
                  onClick={() =>
                    handleViewResultsClick(
                      survey.StudyId,
                      survey.SessionId,
                      survey.SessionCode,
                    )
                  }
                >
                  View Results
                </span>
              </td>
              <td style={tdStyle}>
                <Link
                  to={
                    user?.type === "superadmin"
                      ? `/admin-panel/survey/edit/${lastSegment}?tid=${survey.TriggerId}&session_id=${survey.SessionId}&trigger_type=${survey.TriggerType}&pid=${survey.StudyId}&id=${getClientIdFromUrl(hostname)}&user_type=${user?.type}`
                      : `/dashboard/survey/edit?tid=${survey.TriggerId}&session_id=${survey.SessionId}&trigger_type=${survey.TriggerType}&pid=${survey.StudyId}&id=${getClientIdFromUrl(hostname)}&user_type=${user?.type}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  Edit
                </Link>{" "}
                |{" "}
                {/* <Link
                  to={
                    user?.type === "superadmin"
                      ? `/admin-panel/survey/clone/${lastSegment}?tid=${survey.TriggerId}&session_id=${survey.SessionId}&trigger_type=${survey.TriggerType}&pid=${survey.StudyId}&id=${getClientIdFromUrl(hostname)}&user_type=${user?.type}`
                      : `/dashboard/survey/clone?tid=${survey.TriggerId}&session_id=${survey.SessionId}&trigger_type=${survey.TriggerType}&pid=${survey.StudyId}&id=${getClientIdFromUrl(hostname)}&user_type=${user?.type}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  Clone
                </Link>{" "} */}
                <span
                  style={{
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "#0d6efd",
                  }}
                  onClick={() => handleCloneClick(survey)}
                >
                  Clone
                </span>{" "}
                |{" "}
                <Link
                  to="#"
                  onClick={() => handleDeleteClick(survey)}
                  style={{ textDecoration: "none" }}
                >
                  Delete
                </Link>
              </td>
              <td>
                <>
                  {survey.SessionStatus ? "Active" : "Inactive"}
                  <br />
                  <InputSwitch
                    checked={survey.SessionStatus ? true : false}
                    onChange={(event) => {
                      updateSurveyStatus(
                        event,
                        survey.SessionId,
                        survey.StudyId,
                      );
                    }}
                  />
                </>
              </td>
            </tr>
          ))}
          {/* Dummy row for cloning */}
          {isCloning && (
            <tr id="clonedRow">
              <td></td>
              <td>
                <InputText
                  value={dummySurvey.SessionName}
                  onChange={(e) =>
                    setDummySurvey({
                      ...dummySurvey,
                      SessionName: e.target.value,
                    })
                  }
                  style={{ width: "100%" }}
                />
              </td>
              <td style={{ verticalAlign: "middle" }}>
                <i
                  className={
                    showCloneLoader
                      ? "pi pi-spin pi-spinner"
                      : "pi pi-check-circle"
                  }
                  style={{
                    cursor: showCloneLoader ? "default" : "pointer",
                    color: "#2196f3",
                  }}
                  onClick={!showCloneLoader ? handleAcceptClone : undefined}
                />
                <i
                  className="pi pi-times-circle"
                  style={{
                    cursor: "pointer",
                    color: "#e40505ff",
                    marginLeft: "10px",
                  }}
                  onClick={handleCancelClone}
                />
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderLoadingState = () => (
    <div className="table-responsive">
      {renderSearchAndPagination()}
      <Divider />
      <table className="table table-borderless surveyTable">
        {renderTableHead()}
        <tbody>
          <tr>
            <td colSpan="7" className="text-center py-5">
              <ProgressSpinner />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderEmptyState = () => (
    <div
      className="d-flex flex-column justify-content-center align-items-center text-center"
      style={{ height: "520px" }}
    >
      <p className="text-muted fw-semibold mb-4">
        No XMOTs have been added yet. Use the button <br /> below to add a new
        XMOT.
      </p>
      <Button
        label="Create a Custom XMOT"
        className="px-4 py-2 fw-bold createClientBtn"
        onClick={onAddSurveyClick}
      />
    </div>
  );

  // Dialog Footer Buttons
  const renderDialogFooter = () => (
    <div>
      <Button
        severity="secondary"
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setVisible(false)}
        className="me-2"
      />
      <Button
        severity="danger"
        label="Delete"
        icon="pi pi-trash"
        onClick={() => {
          setVisible(false);
          if (selectedSurvey?.StudyId && selectedSurvey?.SessionId) {
            handleDeleteSurvey(
              selectedSurvey.StudyId,
              selectedSurvey.SessionId,
            );
          }
        }}
      />
    </div>
  );

  const footerContent = (
    <div>
      <Button
        label="Next"
        className="px-3 py-2 fw-bold createClientBtn"
        onClick={handleCreateSurvey}
        autoFocus
      />
    </div>
  );

  return (
    <div className="container-fluid page-wrapper">
      <div className="row">
        <div className="col-12 col-md-12">
          <div className="card shadow-card" style={{ minHeight: "590px" }}>
            {renderHeader()}
            <Divider style={{ margin: "0.5rem 0", padding: "0 1rem" }} />
            {loading
              ? renderLoadingState()
              : surveyData.length > 0
                ? renderSurveyTable()
                : renderEmptyState()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm"
        visible={visible}
        draggable={false}
        style={{ width: "30vw" }}
        onHide={() => setVisible(false)}
        footer={renderDialogFooter()}
      >
        <p className="mt-4 mb-0">
          Are you sure you want to delete{" "}
          <strong>{selectedSurvey?.SessionName}</strong>?
        </p>
      </Dialog>
      <Dialog
        visible={categoryData?.length > 1 && showDialogForCategory}
        modal
        header="Select Category"
        footer={footerContent}
        style={{ width: "30rem" }}
        onHide={() => closeCategoryDialog()}
      >
        <div className="container p-5">
          <label className="pb-1">Select Category</label>
          <select
            className="form-select"
            id="categoryDropdown"
            onChange={handleCategoryChange}
            value={selectedCategory}
          >
            <option value="">Please select a category</option>
            {categoryData?.map((item) => (
              <option
                key={item.category_id}
                value={
                  item.category_id +
                  "-" +
                  item.study_id +
                  "-" +
                  item.category_type
                }
              >
                {item.category_name}
              </option>
            ))}
          </select>
          {showCategoryError && (
            <small className="text-danger">Please select the category</small>
          )}
        </div>
      </Dialog>
      <Toast ref={toast} />
    </div>
  );
};

export default SurveyListOld;
